import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Trophy, ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { getCommissionStats, getCommissionLeaderboard } from '../../../api/kpi';

const TEAL = '#55C5D0';

const formatCurrency = (val) => {
    if (val === null || val === undefined || Number.isNaN(Number(val))) return '—';
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: 0,
    }).format(Number(val));
};

// Australian financial year runs 1 July → 30 June. A financial year is
// identified by its START calendar year (e.g. 2025 = FY2025-26). The default
// selection is derived from today's date, so it automatically "resets" to the
// next financial year on 1 July (no deploy needed).
const fyStartYearForDate = (d = new Date()) =>
    (d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1); // getMonth(): Jul = 6

const fyRange = (startYear) => ({
    start_date: `${startYear}-07-01`,
    end_date: `${startYear + 1}-06-30`,
});

// e.g. 2025 → "FY 2025–26"
const fyLabel = (startYear) => `FY ${startYear}–${String(startYear + 1).slice(2)}`;

/**
 * Office-wide commission summary for the Analytics "Report" tab. Mirrors the
 * headline figures from the Commission Leaderboard page (Total Earned YTD,
 * Total Deals, Total Agents) plus the Top Performers ranking, so admins get the
 * commission picture alongside the other portal reports. This panel is rendered
 * from the admin-only Report view, so it always requests org-wide stats (no
 * agent_id) — the per-agent view lives on the Commissions page itself.
 */
const CommissionReportPanel = () => {
    const [stats, setStats] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);

    // Defaults to the current financial year (auto-rolls to FY26-27 on 1 Jul 2026).
    const [fyStartYear, setFyStartYear] = useState(() => fyStartYearForDate());
    const currentFyStartYear = fyStartYearForDate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const range = fyRange(fyStartYear);
            const [statsRes, leaderboardRes] = await Promise.allSettled([
                getCommissionStats(range),
                getCommissionLeaderboard(range),
            ]);

            if (statsRes.status === 'fulfilled') {
                setStats(statsRes.value.data);
            } else {
                console.error('getCommissionStats failed:', statsRes.reason);
            }
            if (leaderboardRes.status === 'fulfilled') {
                setLeaderboard(leaderboardRes.value.data || []);
            } else {
                console.error('getCommissionLeaderboard failed:', leaderboardRes.reason);
            }

            if (statsRes.status === 'rejected' && leaderboardRes.status === 'rejected') {
                toast.error('Failed to load commission report');
            }
        } finally {
            setLoading(false);
        }
    }, [fyStartYear]);

    useEffect(() => {
        load();
    }, [load]);

    const totalAgents = new Set(
        (leaderboard || []).filter((a) => a.agent_id).map((a) => a.agent_id)
    ).size;

    return (
        <section className="mb-8">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <h2 className="text-lg font-bold text-gray-900">
                        Commissions ({fyLabel(fyStartYear)})
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Office-wide commission earned from sold properties (1 Jul {fyStartYear} – 30 Jun {fyStartYear + 1}), plus the agent leaderboard.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Financial-year switcher — swap between FY25-26 and FY26-27 (and beyond) */}
                    <div className="flex items-center rounded-lg border border-gray-200 bg-white overflow-hidden">
                        <button
                            onClick={() => setFyStartYear((y) => y - 1)}
                            disabled={loading}
                            title="Previous financial year"
                            className="flex items-center justify-center w-9 h-10 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="px-3 text-sm font-semibold text-gray-800 tabular-nums select-none min-w-[92px] text-center">
                            {fyLabel(fyStartYear)}
                        </span>
                        <button
                            onClick={() => setFyStartYear((y) => y + 1)}
                            disabled={loading || fyStartYear >= currentFyStartYear + 1}
                            title="Next financial year"
                            className="flex items-center justify-center w-9 h-10 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-30"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                    {fyStartYear !== currentFyStartYear && (
                        <button
                            onClick={() => setFyStartYear(currentFyStartYear)}
                            disabled={loading}
                            className="text-xs font-medium px-2.5 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            This FY
                        </button>
                    )}
                    <button
                        onClick={load}
                        disabled={loading}
                        title="Refresh commissions"
                        className="flex items-center justify-center w-10 h-10 bg-white text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50 flex-shrink-0"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 sm:p-4 md:p-6">
                    <p className="text-xs sm:text-sm text-gray-400 font-medium mb-1">Total Earned (FY)</p>
                    <p className="text-lg sm:text-xl md:text-2xl font-bold" style={{ color: TEAL }}>
                        {loading && !stats ? '…' : formatCurrency(stats?.ytd_stats?.total_commission)}
                    </p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 sm:p-4 md:p-6">
                    <p className="text-xs sm:text-sm text-gray-400 font-medium mb-1">Total Deals</p>
                    <p className="text-lg sm:text-xl md:text-2xl font-bold" style={{ color: TEAL }}>
                        {loading && !stats ? '…' : (stats?.ytd_stats?.total_count || 0)}
                    </p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 sm:p-4 md:p-6">
                    <p className="text-xs sm:text-sm text-gray-400 font-medium mb-1">Total Agents</p>
                    <p className="text-lg sm:text-xl md:text-2xl font-bold" style={{ color: TEAL }}>
                        {loading && !leaderboard.length ? '…' : totalAgents}
                    </p>
                </div>
            </div>

            {/* Top performers */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 sm:p-4 md:p-6 mt-4">
                <div className="flex items-center gap-2 mb-3 sm:mb-4">
                    <Trophy className="w-5 h-5" style={{ color: TEAL }} />
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Top Performers</h3>
                </div>
                {loading && !leaderboard.length ? (
                    <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="p-3 sm:p-4 bg-gray-50 rounded-lg animate-pulse">
                                <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                            </div>
                        ))}
                    </div>
                ) : leaderboard.length > 0 ? (
                    <div className="space-y-3">
                        {leaderboard.map((entry) => (
                            <div
                                key={entry.agent_id}
                                className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                                    <div
                                        className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full font-bold text-white text-sm sm:text-base flex-shrink-0 ${
                                            entry.rank === 1 ? 'bg-yellow-500'
                                                : entry.rank === 2 ? 'bg-gray-400'
                                                : entry.rank === 3 ? 'bg-orange-600'
                                                : ''
                                        }`}
                                        style={entry.rank > 3 ? { backgroundColor: TEAL } : {}}
                                    >
                                        {entry.rank}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-900 text-sm sm:text-base truncate">{entry.agent_name}</p>
                                        <p className="text-xs sm:text-sm text-gray-500">{entry.commission_count} deals</p>
                                    </div>
                                </div>
                                <div className="text-right ml-2 flex-shrink-0">
                                    <p className="text-base sm:text-lg font-bold" style={{ color: TEAL }}>
                                        {formatCurrency(entry.total_commission)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 px-4 text-gray-500">
                        <p className="text-sm">No commission data available yet</p>
                    </div>
                )}
            </div>
        </section>
    );
};

export default CommissionReportPanel;
